<script lang="ts" setup>
	import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/vue'
	import { ref, watch } from 'vue'
	import { useRoute } from 'vue-router'

	import BreadCrumb from '@/components/BreadCrumb.vue'
	import LoadingModal from '@/components/LoadingModal.vue'

	import useHasPermissionUser from '@/composables/useHasPermissionUser'

	import useChain from './composables/useChain'
	import InformationGeneral from './partials/InformationGeneral.vue'
	import LisOfBrands from './partials/LisOfBrands.vue'
	import LoyaltyProgram from './partials/LoyaltyProgram.vue'
	import RatePlaneCodesTab from './partials/RatePlaneCodeTab.vue'

	const route = useRoute()

	const { data, isLoading } = useChain({
		immediate: true,
		params: {
			id: route.params.id as string,
		},
	})

	const { hasPermissionUser } = useHasPermissionUser()

	const tabs = ref([
		{
			name: 'Informacion General',
			component: InformationGeneral,
			permissionName: ['update-internal', 'create-medias-internal'],
			module: ['App\\Models\\Chain', 'App\\Models\\Chain'],
		},
		{
			component: RatePlaneCodesTab,
			name: 'Códigos de tarifas',
			permissionName: ['update-internal'],
			module: ['App\\Models\\Chain'],
		},
		{
			name: 'Listado de marcas',
			component: LisOfBrands,
			permissionName: ['viewAny-internal'],
			module: ['App\\Models\\Chain'],
		},
		{
			name: 'Programas de lealtad',
			component: LoyaltyProgram,
			permissionName: ['view-loyaltyPrograms-internal'],
			module: ['App\\Models\\Chain'],
		},
	])

	const pages = ref([
		{
			name: 'Proveedores de hoteleria',
			to: {
				name: 'hotel',
			},
		},
		{
			name: 'Cadenas',
			to: {
				name: 'hotel.chain',
			},
		},
		{
			name: 'Editar ',
			to: {
				name: 'hotel.chain.edit',
				params: {
					id: route.params.id as string,
				},
			},
		},
	])

	watch(
		data,
		(value) => {
			if (value) {
				pages.value[2].name = `Editar la cadena ${value.name}`
			}
		},
		{
			immediate: true,
		}
	)
</script>

<template>
	<BreadCrumb :pages="pages" />
	<TabGroup
		:default-index="
			route.query.tab ?
				tabs.findIndex((tab) => tab.name === route.query.tab)
			:	undefined
		"
		as="div"
		class="flex flex-col lg:flex-row gap-6 max-w-7xl mx-auto px-4 lg:px-[2.75rem] py-2 mb-7"
		v-if="!isLoading"
	>
		<TabList
			as="aside"
			class="flex flex-col gap-4 lg:w-1/3 lg:max-w-[13.75rem] !lg:sticky mt-8"
		>
			<nav
				v-dragscroll.x
				class="no-scrollbar flex touch-pan-x px-2 lg:p-0 w-full lg:max-w-[13.75rem] flex-row lg:flex-col gap-2 overflow-x-auto"
			>
				<Tab
					v-for="(tab, i) in tabs.filter((tab, i) =>
						tab.permissionName?.some((permission) =>
							hasPermissionUser(permission, tab.module?.[i])
						)
					)"
					:key="i"
					v-slot="{ selected }"
					class="rounded-md w-[13.75rem]"
				>
					<button
						:class="[
							'w-full py-3 px-2 font-medium whitespace-nowrap rounded-md text-white focus:outline-none select-none',
							selected ? 'bg-[#253C70]' : 'bg-[#1F2937]',
						]"
					>
						{{ tab.name }}
					</button>
				</Tab>
			</nav>
		</TabList>

		<TabPanels
			as="div"
			class="w-full"
		>
			<TabPanel
				v-for="Element in tabs.filter((tab, i) =>
					tab.permissionName?.some((permission) =>
						hasPermissionUser(permission, tab.module?.[i])
					)
				)"
				:key="Element.name"
			>
				<component
					:is="Element.component"
					:chain="data!"
				/>
			</TabPanel>
		</TabPanels>
	</TabGroup>

	<LoadingModal :when="isLoading" />
</template>
